import { z } from 'zod';

export const assetCategories = [
  'Laptop', 'Desktop', 'Tablet', 'Mobile', 'MFD', 'Server', 'Network', 'SIM', 'Other',
] as const;

// Must stay in step with CK_Asset_LifecycleState (migration 068), the
// LifecycleState union in types/oscar.ts, and the LifecycleStates array in
// oscar/api/Functions/AssetsFunctions.cs. FOUR copies of one list — adding a
// state means touching all four, and the CHECK constraint is the authority.
export const lifecycleStates = [
  'Ordered', 'Available at BTG', 'Available at Branch', 'Spare / Ready to Deploy',
  'Reserved - Employee Not Set Up',
  'Deployed', 'In Repair / Warranty Claim', 'Lost or Stolen',
  // Set from OUTSIDE Oscar by the Manage.Core hardware request form. Offered
  // here so somebody can move an asset OUT of it once the replacement lands —
  // otherwise the state is a trap only an external system can set.
  'Replacement Requested',
  'Return to BTG', 'Return in Transit to BTG', 'Return Received by BTG',
  'Dispose Pending by Branch', 'Dispose Pending by BTG', 'Disposed by Branch', 'Disposed by BTG',
  'Archived',
] as const;

const assetTagRe = /^ORG-[0-9]{4}$/;

// Core typed columns. Per-category custom fields (Attributes) are driven by
// oscar.AssetFieldDefinition and validated server-side, so they live outside
// this static schema (see EditForm's dynamic field block).
export const assetSchema = z
  .object({
    SerialNumber: z.string().trim().optional(),
    Category: z.enum(assetCategories),
    AssetTag: z.string().trim().optional().refine((v) => !v || assetTagRe.test(v), 'Asset tag must match ORG-XXXX'),
    Make: z.string().trim().optional(),
    Model: z.string().trim().optional(),
    Hostname: z.string().trim().optional(),
    Msisdn: z.string().trim().optional(),
    PurchaseDate: z.string().trim().optional(),
    PurchasePrice: z.number().nonnegative().nullable().optional(),
    Supplier: z.string().trim().optional(),
    PoReference: z.string().trim().optional(),
    WarrantyExpiry: z.string().trim().optional(),
    LifecycleState: z.enum(lifecycleStates),
    LocationId: z.number().int().positive().nullable().optional(),
    PriorUser: z.string().trim().max(200, 'Keep it under 200 characters').optional(),
    Notes: z.string().trim().optional(),
  });

export type AssetFormData = z.infer<typeof assetSchema>;
